import React from 'react';
import styled from 'styled-components';
import { alpha, Button, Typography } from '@material-ui/core';

import { useTimeout } from '../timeout.hook';

export const StyledDragArea = styled.div`
  position: relative;
  border-radius: 2px;
  border: 3px dashed ${props => alpha(props.theme.palette.primary.main, 0)};
  transition: background-color 300ms cubic-bezier(0.075, 0.82, 0.165, 1),
    border 300ms cubic-bezier(0.075, 0.82, 0.165, 1);
  &.dragging {
    border: 3px dashed ${props => props.theme.palette.primary.main};
    background-color: ${props => alpha(props.theme.palette.primary.main, 0.12)};
    .drag-empty-container {
      border: 1px solid ${props => alpha(props.theme.palette.divider, 0)};
    }
  }
  .hidden {
    display: none;
  }
  .drag-empty-container {
    &:not(.hidden) {
      display: flex;
    }
    padding: 16px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: 2px;
    border: 1px solid ${props => props.theme.palette.divider};
    transition: border 300ms cubic-bezier(0.075, 0.82, 0.165, 1);
  }
  .drag-text,
  .drag-button {
    margin: 10px 0;
  }
`;

export interface DragAreaProps {
  dragOver: boolean;
  disabled?: boolean;
  dragLabel?: React.ReactNode;
  buttonLabel?: string;
  className?: string;
  hideArea?: boolean;
  onClick?: () => void;
  onUpload?: (files: FileList | null) => void;
  setDragOver: (dragOver: boolean) => void;
}

const DragArea: React.FC<DragAreaProps> = props => {
  const {
    dragOver,
    disabled,
    hideArea,
    className,
    children,
    dragLabel = 'Drag one or more files to upload',
    buttonLabel = 'Select Files',
    onClick,
    onUpload,
    setDragOver
  } = props;
  const [debounceLeave, clearDebouncedLeave] = useTimeout();
  const classes: string[] = [];
  className && classes.push(className);
  dragOver && classes.push('dragging');
  const areaClasses = ['drag-empty-container'];
  hideArea && areaClasses.push('hidden');
  return (
    <StyledDragArea
      className={classes.join(' ')}
      onDragOver={event => {
        clearDebouncedLeave();
        if (!disabled) {
          event.preventDefault();
          setDragOver(true);
        }
      }}
      onDragLeave={() => {
        // delay set to false to avoid the area from flickering
        debounceLeave(() => {
          !disabled && setDragOver(false);
        }, 50);
      }}
      onDrop={event => {
        clearDebouncedLeave();
        if (!disabled) {
          event.preventDefault();
          setDragOver(false);
          onUpload?.(event.dataTransfer.files);
        }
      }}
    >
      <div className={areaClasses.join(' ')}>
        <Typography variant='subtitle2' className='drag-text'>
          {dragLabel}
        </Typography>
        <Typography variant='subtitle2' className='or-text'>
          -- or --
        </Typography>
        <Button
          color='primary'
          variant='outlined'
          className='drag-button'
          disabled={disabled}
          onClick={onClick}
        >
          {buttonLabel}
        </Button>
      </div>
      {children}
    </StyledDragArea>
  );
};

export default DragArea;
